import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, Pencil, Save, X, RefreshCw, Plus, MoreVertical, Archive, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, PageShell } from "@/components/PageHeader";

/**
 * Admin → Quote Rates (Master Rate Catalog · Phase 3.C · Option B Full Drain).
 *
 * • Read-gated by `pricing.view`.
 * • Write-gated server-side by `pricing.manage_rate_catalog`.
 * • Protected rows (Payroll 28%, EOT Base/Surcharge/Combined) show a
 *   padlock but stay editable — `protected_actions` govern which
 *   fields are locked (`cannot_change_key`, `cannot_change_taxability`)
 *   and the row cannot be deleted / archived.
 * • Every protected-row Save triggers an Enhanced Confirmation Dialog.
 * • Cross-tab invalidation: watches `X-Catalog-Revision` on every API
 *   response; if it changes underneath, we reload automatically.
 * • No destructive "Delete X" button anywhere. Archive lives behind a
 *   "More" affordance with its own confirmation.
 */

const CATEGORY_LABELS = {
  equipment: "Equipment",
  drivers: "Drivers",
  communications: "Communications",
  mileage: "Mileage",
  generator_service: "Engine / Generator",
  cleaning: "Cleaning",
  utilities: "Utilities",
  fees: "Fees",
  custom: "Miscellaneous & Other",
};

const CATEGORY_ORDER = [
  "equipment", "drivers", "communications", "mileage",
  "generator_service", "cleaning", "utilities", "fees", "custom",
];

const UNIT_LABELS = {
  flat: "Flat",
  per_day: "Per Day",
  per_week: "Per Week",
  per_month: "Per Month",
  per_mile: "Per Mile",
  per_trip: "Per Trip",
  each: "Each",
  percent: "%",
};

function formatRate(r) {
  if (r == null) return "—";
  const n = Number(r);
  if (Number.isNaN(n)) return String(r);
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function summarizeFormula(row) {
  const meta = row.formula_meta || {};
  const inputs = Array.isArray(meta.inputs) ? meta.inputs : [];
  const ftype = meta.formula_type || row.formula_type || "—";
  if (ftype === "PERCENTAGE_OF" && inputs[0]?.source) {
    return `${formatRate(row.rate)}% × ${String(inputs[0].source).replace(/_/g, " ")}`;
  }
  if (ftype === "BASE_PLUS_SURCHARGE") {
    const base = inputs.find(i => i.role === "base")?.key || "base";
    const surch = inputs.find(i => i.role === "surcharge")?.key || "surcharge";
    return `${base} + ${surch}`;
  }
  if (ftype === "FLAT") return `$${formatRate(row.rate)}`;
  return String(ftype);
}

export default function AdminQuoteRates() {
  const { user } = useAuth();
  const canWrite = true; // backend authoritative
  const [rows, setRows] = useState(null);
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { row, before, after, submit }
  const [addOpen, setAddOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(null); // row
  const [openMenu, setOpenMenu] = useState(null);
  const revisionRef = useRef(-1);

  const load = async () => {
    try {
      const r = await api.get(
        `/pricing/rate-catalog${includeLegacy ? "?include_legacy=true" : ""}`,
      );
      const rev = Number(r.headers?.["x-catalog-revision"] ?? -1);
      revisionRef.current = rev;
      setRows(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeLegacy]);

  // Cross-tab invalidation poll — cheap /meta endpoint every 8s.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get("/pricing/rate-catalog/meta");
        const rev = Number(r.data?.catalog_revision ?? -1);
        if (revisionRef.current >= 0 && rev > revisionRef.current) {
          revisionRef.current = rev;
          load();
        }
      } catch (_) { /* silent */ }
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const grouped = useMemo(() => {
    if (!rows) return {};
    const m = {};
    for (const r of rows) (m[r.category] ??= []).push(r);
    return m;
  }, [rows]);

  const submitPatch = async (row, patch) => {
    try {
      const resp = await api.patch(`/pricing/rate-catalog/${row.id}`, patch);
      const rev = Number(resp.headers?.["x-catalog-revision"] ?? -1);
      if (rev > 0) revisionRef.current = rev;
      toast.success(`Updated · Draft quotes will recalculate on next refresh.`);
      setEditing(null);
      setConfirmDialog(null);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "protected_action_denied") {
        toast.error(`Protected: ${d.action}`);
      } else if (d?.code === "formula_self_reference"
              || d?.code === "formula_circular_dependency"
              || d?.code === "formula_invalid"
              || d?.code === "formula_unknown_reference") {
        toast.error(d.message || d.code);
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const requestSave = (row) => {
    const patch = {
      rate: editing.rate === "" ? null : editing.rate,
      display_name: editing.display_name,
    };
    // Only push taxable/active if changed & not protected.
    if (!row.protected_actions?.includes("cannot_change_taxability")) {
      patch.taxable = editing.taxable;
    }
    patch.active = editing.active;
    // Protected? show Enhanced Confirmation.
    if (row.protected) {
      setConfirmDialog({
        row,
        before: {
          rate: row.rate,
          taxable: row.taxable,
          active: row.active,
          display_name: row.display_name,
        },
        after: patch,
        submit: () => submitPatch(row, patch),
      });
      return;
    }
    submitPatch(row, patch);
  };

  const doArchive = async (row) => {
    try {
      await api.post(`/pricing/rate-catalog/${row.id}/archive`);
      toast.success(`Archived · ${row.display_name}`);
      setArchiveConfirm(null);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "formula_dependency_in_use") {
        toast.error(`Cannot archive — used by: ${(d.dependents || []).join(", ")}`);
      } else if (d?.code === "protected_action_denied") {
        toast.error("Protected system row — archive denied.");
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const doRestore = async (row) => {
    try {
      await api.post(`/pricing/rate-catalog/${row.id}/restore`);
      toast.success(`Restored · ${row.display_name}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell>
      <PageHeader
        testid="page-quote-rates"
        title="Quote Rates"
        subtitle="Master Rate Catalog — protected rows (Payroll, EOT) are DB-backed. Sent / accepted quotes are frozen and are not affected by edits here."
        right={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[12px] text-steel">
              <input type="checkbox" checked={includeLegacy}
                     onChange={(e) => setIncludeLegacy(e.target.checked)}
                     data-testid="catalog-show-legacy" />
              Show Legacy / Archived
            </label>
            <button onClick={load} data-testid="catalog-refresh"
                    className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline">
              <RefreshCw size={12} /> Refresh
            </button>
            <button onClick={() => setAddOpen(true)} data-testid="catalog-add-new"
                    className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded bg-coach-blue text-white hover:bg-deep-road-navy">
              <Plus size={12} /> Add Rate Line
            </button>
          </div>
        } />

      <div className="space-y-6 mt-2" data-testid="admin-quote-rates">
        {rows === null && <div className="text-steel text-[13px]">Loading…</div>}
        {rows && CATEGORY_ORDER.map((cat) => {
          const entries = grouped[cat];
          if (!entries || entries.length === 0) return null;
          return (
            <section key={cat} className="bg-white border border-coach-silver rounded-md">
              <div className="px-4 py-2 border-b border-coach-silver bg-road-white">
                <div className="text-[12.5px] font-medium text-carbon">
                  {CATEGORY_LABELS[cat] || cat}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="text-[10.5px] uppercase tracking-widest text-steel">
                    <tr>
                      <th className="text-left px-4 py-2">Service</th>
                      <th className="text-left px-4 py-2 w-28">Unit</th>
                      <th className="text-right px-4 py-2 w-32">Rate</th>
                      <th className="text-left px-4 py-2 w-24">Taxable</th>
                      <th className="text-left px-4 py-2 w-24">Active</th>
                      <th className="text-left px-4 py-2 w-64 hidden md:table-cell">Formula</th>
                      {canWrite && <th className="text-right px-4 py-2 w-32"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((r) => (
                      <RateRow key={r.id} r={r} canWrite={canWrite}
                        editing={editing?.id === r.id ? editing : null}
                        menuOpen={openMenu === r.id}
                        toggleMenu={() => setOpenMenu(openMenu === r.id ? null : r.id)}
                        onEdit={() => setEditing({
                          id: r.id, rate: r.rate ?? "",
                          taxable: !!r.taxable, active: !!r.active,
                          display_name: r.display_name,
                        })}
                        onCancel={() => setEditing(null)}
                        onChange={(patch) => setEditing((p) => ({ ...p, ...patch }))}
                        onSave={() => requestSave(r)}
                        onArchive={() => { setOpenMenu(null); setArchiveConfirm(r); }}
                        onRestore={() => doRestore(r)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-6 text-[11px] text-steel italic">
        Protected system rows (padlock) — Payroll Fee, End of Tour Cleaning Base, Trailer Cleaning
        Surcharge, and the derived Coach+Trailer combined line — cannot be deleted, archived,
        renamed, or have their taxability changed. Rate and formula edits ARE allowed and take
        effect on the next Draft-quote fetch. Sent Quote PDFs remain byte-identical.
      </div>

      {confirmDialog && (
        <EnhancedConfirmDialog
          {...confirmDialog}
          onCancel={() => setConfirmDialog(null)} />
      )}
      {archiveConfirm && (
        <ArchiveConfirmDialog
          row={archiveConfirm}
          onCancel={() => setArchiveConfirm(null)}
          onConfirm={() => doArchive(archiveConfirm)} />
      )}
      {addOpen && (
        <AddRateLineDialog
          onCancel={() => setAddOpen(false)}
          onCreated={async () => { setAddOpen(false); await load(); }} />
      )}
    </PageShell>
  );
}

function RateRow({
  r, canWrite, editing, menuOpen, toggleMenu,
  onEdit, onCancel, onChange, onSave, onArchive, onRestore,
}) {
  const isProtected = !!r.protected || !!r.locked;
  const isArchived = !!r.is_archived;
  const isEditing = editing !== null;
  const rateDisplay = r.rate == null
    ? "—"
    : `${formatRate(r.rate)}${r.unit === "percent" ? "%" : ""}`;
  return (
    <tr data-testid={`catalog-row-${r.key}`} className="border-t border-coach-silver/60">
      <td className="px-4 py-2">
        <div className="text-carbon">{r.display_name}</div>
        <div className="text-[10.5px] font-mono text-steel">{r.key}</div>
        {isProtected && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest text-amber-hold"
                data-testid={`catalog-protected-${r.key}`}>
            <Lock size={10} /> protected
          </span>
        )}
        {isArchived && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest text-critical-red"
                data-testid={`catalog-archived-${r.key}`}>
            archived
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-steel">{UNIT_LABELS[r.unit] || r.unit}</td>
      <td className="px-4 py-2 text-right tabular font-mono">
        {isEditing ? (
          <input type="number" step="0.01"
                 value={editing.rate ?? ""}
                 onChange={(e) => onChange({ rate: e.target.value })}
                 data-testid={`catalog-rate-input-${r.key}`}
                 className="w-24 border border-coach-silver rounded-sm px-2 py-1 text-right" />
        ) : rateDisplay}
      </td>
      <td className="px-4 py-2 text-steel">
        {isEditing && !r.protected_actions?.includes("cannot_change_taxability") ? (
          <input type="checkbox" checked={editing.taxable}
                 onChange={(e) => onChange({ taxable: e.target.checked })}
                 data-testid={`catalog-taxable-${r.key}`} />
        ) : (r.taxable ? "Yes" : "No")}
      </td>
      <td className="px-4 py-2 text-steel">
        {isEditing ? (
          <input type="checkbox" checked={editing.active}
                 onChange={(e) => onChange({ active: e.target.checked })}
                 data-testid={`catalog-active-${r.key}`} />
        ) : (r.active ? "Yes" : "Legacy")}
      </td>
      <td className="px-4 py-2 text-steel hidden md:table-cell text-[12px]">
        {summarizeFormula(r)}
      </td>
      {canWrite && (
        <td className="px-4 py-2 text-right">
          {isEditing ? (
            <div className="inline-flex items-center gap-2">
              <button onClick={onCancel} className="text-[12px] text-steel"
                      data-testid={`catalog-cancel-${r.key}`}><X size={12} /></button>
              <button onClick={onSave}
                      className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline"
                      data-testid={`catalog-save-${r.key}`}>
                <Save size={12} /> Save Changes
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-3 relative">
              <button onClick={onEdit}
                      className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline"
                      data-testid={`catalog-edit-${r.key}`}>
                <Pencil size={12} /> Edit
              </button>
              {(!isProtected || isArchived) && (
                <button onClick={toggleMenu}
                        className="text-steel hover:text-carbon"
                        data-testid={`catalog-more-${r.key}`}>
                  <MoreVertical size={14} />
                </button>
              )}
              {menuOpen && (
                <div className="absolute right-0 top-6 z-10 bg-white border border-coach-silver rounded-md shadow-md text-[12px] min-w-[140px]"
                     data-testid={`catalog-more-menu-${r.key}`}>
                  {!isArchived && !isProtected && (
                    <button onClick={onArchive}
                            className="w-full text-left px-3 py-1.5 hover:bg-road-white flex items-center gap-2 text-critical-red"
                            data-testid={`catalog-archive-${r.key}`}>
                      <Archive size={12} /> Archive
                    </button>
                  )}
                  {isArchived && (
                    <button onClick={onRestore}
                            className="w-full text-left px-3 py-1.5 hover:bg-road-white flex items-center gap-2 text-coach-blue"
                            data-testid={`catalog-restore-${r.key}`}>
                      <Undo2 size={12} /> Restore
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

function EnhancedConfirmDialog({ row, before, after, submit, onCancel }) {
  const label = row.key === "payroll_fee" ? "PAYROLL FORMULA" : `${row.display_name.toUpperCase()}`;
  const beforeText = row.key === "payroll_fee"
    ? `${before.rate}% × Total Driver Wages`
    : `$${formatRate(before.rate)}`;
  const afterText = row.key === "payroll_fee"
    ? `${after.rate}% × Total Driver Wages`
    : `$${formatRate(after.rate)}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/60"
         data-testid="catalog-enhanced-confirm">
      <div className="bg-white border border-coach-silver rounded-lg shadow-xl w-[420px] p-6">
        <div className="text-[14px] font-medium text-carbon">CHANGE {label}?</div>
        <div className="mt-4 text-[12px] uppercase tracking-widest text-steel">CURRENT</div>
        <div className="text-[15px] text-carbon" data-testid="catalog-confirm-before">{beforeText}</div>
        <div className="mt-3 text-[12px] uppercase tracking-widest text-steel">NEW</div>
        <div className="text-[15px] text-coach-blue" data-testid="catalog-confirm-after">{afterText}</div>
        <div className="mt-4 text-[11px] text-steel italic">
          This will recalculate all active Draft Quotes. Sent and historical Quote versions
          will remain unchanged.
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onCancel} className="text-[12px] text-steel px-3 py-1.5"
                  data-testid="catalog-confirm-cancel">Cancel</button>
          <button onClick={submit}
                  className="text-[12px] text-white bg-coach-blue px-3 py-1.5 rounded hover:bg-deep-road-navy"
                  data-testid="catalog-confirm-submit">
            Confirm Change
          </button>
        </div>
      </div>
    </div>
  );
}

function ArchiveConfirmDialog({ row, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/60">
      <div className="bg-white border border-coach-silver rounded-lg shadow-xl w-[420px] p-6"
           data-testid="catalog-archive-confirm">
        <div className="text-[14px] font-medium text-carbon">Archive {row.display_name}?</div>
        <div className="mt-3 text-[12px] text-steel">
          The row will be hidden from Quote Builder catalog. It can be restored later.
          If any other active line depends on it, archive is refused.
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onCancel} className="text-[12px] text-steel px-3 py-1.5"
                  data-testid="catalog-archive-cancel">Cancel</button>
          <button onClick={onConfirm}
                  className="text-[12px] text-white bg-critical-red px-3 py-1.5 rounded hover:opacity-90"
                  data-testid="catalog-archive-submit">
            Confirm Archive
          </button>
        </div>
      </div>
    </div>
  );
}

function AddRateLineDialog({ onCancel, onCreated }) {
  const [form, setForm] = useState({
    display_name: "",
    category: "custom",
    unit: "flat",
    rate: "",
    taxable: false,
    formula_type: "FLAT",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.display_name.trim()) {
      toast.error("Display name is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        display_name: form.display_name.trim(),
        category: form.category,
        unit: form.unit,
        rate: form.rate === "" ? null : form.rate,
        taxable: !!form.taxable,
        formula_type: form.formula_type,
        formula_meta: { formula_type: form.formula_type, inputs: [] },
      };
      await api.post("/pricing/rate-catalog", body);
      toast.success(`Created · ${form.display_name}`);
      await onCreated();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/60">
      <div className="bg-white border border-coach-silver rounded-lg shadow-xl w-[480px] p-6"
           data-testid="catalog-add-dialog">
        <div className="text-[14px] font-medium text-carbon">Add Rate Line</div>
        <div className="mt-4 space-y-3 text-[13px]">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-steel">Display Name</div>
            <input type="text" value={form.display_name}
                   onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                   data-testid="catalog-add-display-name"
                   className="w-full border border-coach-silver rounded px-2 py-1 mt-1" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-steel">Category</div>
              <select value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      data-testid="catalog-add-category"
                      className="w-full border border-coach-silver rounded px-2 py-1 mt-1">
                {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-steel">Unit</div>
              <select value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      data-testid="catalog-add-unit"
                      className="w-full border border-coach-silver rounded px-2 py-1 mt-1">
                {Object.entries(UNIT_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-steel">Rate</div>
              <input type="number" step="0.01" value={form.rate}
                     onChange={(e) => setForm({ ...form, rate: e.target.value })}
                     data-testid="catalog-add-rate"
                     className="w-full border border-coach-silver rounded px-2 py-1 mt-1" />
            </div>
            <div className="flex-1 flex items-end pb-2">
              <label className="flex items-center gap-2 text-[12px] text-steel">
                <input type="checkbox" checked={form.taxable}
                       onChange={(e) => setForm({ ...form, taxable: e.target.checked })}
                       data-testid="catalog-add-taxable" />
                Taxable
              </label>
            </div>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onCancel} className="text-[12px] text-steel px-3 py-1.5"
                  data-testid="catalog-add-cancel">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="text-[12px] text-white bg-coach-blue px-3 py-1.5 rounded hover:bg-deep-road-navy disabled:opacity-50"
                  data-testid="catalog-add-submit">
            {busy ? "Saving…" : "Create Rate Line"}
          </button>
        </div>
      </div>
    </div>
  );
}
